import { useEffect, useState, useCallback } from "react";
import { useTheme, InputLabel, TextField, FormControl, Select, MenuItem, Typography, CircularProgress, InputAdornment, IconButton, ListItemText, FormHelperText } from "@mui/material";
import { Search } from "react-bootstrap-icons";
import { grey } from "@mui/material/colors";
import { ISearchableSelect } from "src/types/atoms";
const SearchableSelect = ({
  label,
  placeholder = "",
  loading = "Cargando",
  errorMessage = "Hubo un error",
  idKey = "id",
  primaryKey,
  secondaryKey = "",
  query,
  register = {},
  watchValue = null,
  reset = () => null,
  helperText,
  disabled = false
}: ISearchableSelect) => {
  const [value, setValue] = useState("-1");
  const [options, setOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [initialized, setInitialized] = useState(false);
  const rowsPerPage = 10;
  const theme = useTheme();
  const getOptions = useCallback(async (localSearch = "") => {
    try {
      if (initialized && options.length > 0 && localSearch === search) {
        return;
      }
      setIsLoading(true);
      const res = await query(rowsPerPage, localSearch);
      setOptions(Array.isArray(res) ? [...res] : [...res.data.items]);
      setSearch(localSearch);
      setError(false);
      setInitialized(true);
    } catch (error) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [query]);
  const handleSearch = useCallback((localSearch = "") => {
    setValue("-1");
    reset();
    getOptions(localSearch);
  }, [getOptions, reset]);
  useEffect(() => {
    getOptions(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getOptions]);
  useEffect(() => {
    if (watchValue === "-1") {
      setValue("-1");
      reset();
      setSearch("");
      handleSearch("");
    }
  }, [watchValue, reset, handleSearch]);
  const onKeyPress = (e: any) => {
    e.stopPropagation();
  };
  const onPressEnter = (e: any) => {
    if (e.keyCode === 13) {
      handleSearch(search);
    }
  };
  const classes = {
    root: {
      "&:has(input[value='-1'])": {
        color: grey[400]
      }
    },
    label: {
      color: grey[900],
      marginBottom: 0.5
    },
    searchInput: {
      "& input": {
        height: 17
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: `${grey[400]} !important`,
        borderWidth: "1px !important"
      }
    },
    progress: {
      ml: 2,
      verticalAlign: "text-bottom"
    }
  };
  return <>
      <InputLabel htmlFor="client" sx={classes.label}>
        {label}
      </InputLabel>
      <FormControl fullWidth>
        <Select defaultValue="-1" sx={classes.root} disabled={disabled} renderValue={(value: string) => {
        const json = JSON.parse(value);
        return isNaN(json) ? json[primaryKey] : placeholder;
      }} inputProps={{
        value,
        onChange: (e: any) => setValue(e.target.value)
      }} {...register}>
          {isLoading ? [<li key="loading" value="">
                  <Typography textAlign="center" paddingY={3}>
                    {loading}
                    <CircularProgress size={25} sx={classes.progress} />
                  </Typography>
                </li>, <MenuItem key="defaultValue" value="-1" sx={{
          display: "none"
        }}>
                  {placeholder}
                </MenuItem>] : error ? [<li key="error" value="">
                  <Typography textAlign="center" paddingY={3} color="error">
                    {errorMessage}
                  </Typography>
                </li>, <MenuItem key="defaultValue" value="-1" sx={{
          display: "none"
        }}>
                  {placeholder}
                </MenuItem>] : [<FormControl key="searchInput" onKeyDown={onKeyPress} fullWidth sx={{
          px: 1,
          mb: 2
        }}>
                  <TextField placeholder="Buscar" sx={classes.searchInput} autoComplete="off" value={search} onKeyDown={onPressEnter} onChange={e => setSearch(e.target.value)} InputProps={{
            endAdornment: <InputAdornment position="end">
                          <IconButton edge="end" onClick={() => handleSearch(search)}>
                            <Search color={theme.palette.primary.main} size="1.1rem" />
                          </IconButton>
                        </InputAdornment>
          }} />
                </FormControl>, <MenuItem key="defaultValue" value="-1" sx={{
          display: "none"
        }}>
                  {placeholder}
                </MenuItem>, options.map(option => <MenuItem key={`option-${option[idKey]}`} value={JSON.stringify(option)}>
                    <ListItemText primary={option[primaryKey]} secondary={option[secondaryKey]} />
                  </MenuItem>)]}
        </Select>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
    </>;
};
export default SearchableSelect;